import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Sparkles } from 'lucide-react'
import { Seo } from '@/sanskrit/components/generals/seo'
import { Layout } from '@/sanskrit/components/generals/layout'
import { PageHeader } from '@/sanskrit/components/vani/bits'
import { useAppState, useT } from '@/sanskrit/components/providers/app-state'
import { LESSONS } from '@/sanskrit/lib/content/lessons'
import { nextLesson } from '@/sanskrit/lib/progress/engine'
import CurriculumMap from '@/sanskrit/components/lesson/curriculum-map'

const LearnPage: FC = (): ReactNode => {
    const t = useT()
    const { progress, settings, hydrated } = useAppState()
    const next = hydrated ? nextLesson(progress, { knowsDevanagari: settings.knowsDevanagari }) : LESSONS[0]
    const passed = Object.values(progress.lessons).filter(l => l.passed).length

    return (
        <Layout>
            <Seo title='Course map' description='A structured Sanskrit course from the alphabet to your first story: 22 guided lessons with exercises, plus labs for every grammar topic.' />

            <PageHeader
                sa='पाठ्यक्रमः'
                title={{ hi: 'संस्कृत पाठ्यक्रम', en: 'Your Sanskrit course' }}
                subtitle={{
                    hi: 'हर पाठ 3 से 8 मिनट का है: देखिए, सुनिए, समझिए, अभ्यास कीजिए। एक पाठ पूरा करने पर अगला खुलता है।',
                    en: 'Each lesson takes 3 to 8 minutes: watch, listen, understand, practise. Passing a lesson unlocks the next one.'
                }}
            >
                <div className='mt-6 flex flex-wrap items-center gap-3'>
                    {next && (
                        <Link href={`/sanskrit/learn/${next.id}`} className='inline-flex h-12 items-center gap-2 rounded-2xl bg-saffron px-5 text-base font-bold text-ink shadow-md hover:brightness-105'>
                            <Sparkles className='size-5' />
                            {passed === 0 ? t({ hi: 'पहला पाठ शुरू करें', en: 'Start the first lesson' }) : t({ hi: 'जारी रखें:', en: 'Continue:' })}
                            {passed > 0 && <span lang='sa' className='deva'>{next.sa}</span>}
                        </Link>
                    )}
                    <p className='text-sm text-muted-foreground'>
                        {t({ hi: `${LESSONS.length} में से ${passed} पाठ पूरे`, en: `${passed} of ${LESSONS.length} lessons complete` })}
                    </p>
                </div>
            </PageHeader>

            <div className='mx-auto max-w-4xl px-4 pb-16 sm:px-6'>
                <CurriculumMap />
            </div>
        </Layout>
    )
}

export default LearnPage
